import React, {
  useState,
  useContext,
  useMemo,
} from "react";

import { useIdleTimer } from "react-idle-timer";
import Logger from "../../util/logger";
import { Config, useSession } from "./session";
import Call from "../../util/call";
import { LoginBody } from "../../../../../types";
import useSafeTranslation from "../../util/useSafeTranslation";
import { enRoutes, frRoutes } from "../../resources/routes";

const logger = Logger("auth-provider");

export const AuthContext = React.createContext({} as AuthContextValue);

export interface Token {
  payload: any,
  jwtToken?: string
}

export interface UserDetails {
  invite: string,
  userID: string,
  email: string,
  dob?: string
}
interface AuthContextValue {
  token?: Token,
  auth: boolean,
  sessionExpired: boolean,
  firstName: string,
  userDetails: UserDetails | null,
  lastLoginTime: number | null,
  inviteStatus: string,
  setInviteStatus(status: string): void,
  login(invite: string, userType: string, userID: string, email: string, dob?: string): Promise<string>,
  logout(x: boolean): boolean,
  resetAuth(): void,
  isLoggingOut: boolean
}

interface AuthResponse {
  success: boolean,
  limit?: boolean,
}
interface LoginResult extends AuthResponse{
  token?: Token
}

/**
 * Profile context
 */
export const AuthProvider: React.FC = (props) => {
  const { config } = useSession() as { config: Config };
  const [token, setToken] = useState<Token>();
  const [auth, setAuth] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [inviteStatus, setInviteStatus] = useState<string>("");
  const [isLoggingOut,setIsLoggingOut] = useState(false);
  const timeout = config.SESSION_TIMEOUT;
  const { t } = useSafeTranslation("common");

  const lastLoginTime = useMemo(
    () =>
      token &&
      token.payload &&
      token.payload["custom:last_login"] &&
      !isNaN(token.payload["custom:last_login"])
        ? parseInt(token.payload["custom:last_login"])
        : null,
    [token]
  );

  const logout = (x: boolean) => {
    //leave incase we need a "are you sure" pop up
    if (x) {
      setIsLoggingOut(true);
      setAuth(false);
      window.location.href = t("links:external.ircc.link") as string;
      return x;
    }
    return x;
  };

  const resetAuth = () => {
    setAuth(false)
  }

  const handleOnIdle = () => {
    if(userDetails?.invite) {
      const refreshPath = window.location.href.includes("/fr/")
        ? frRoutes.login.replace(":invite", userDetails.invite)
        : enRoutes.login.replace(":invite", userDetails.invite);
      const refreshUrl = window.location.origin + refreshPath
      window.location.href = refreshUrl
    } else {
     window.location.reload();
    }
  }
  useIdleTimer({
    timeout,
    onIdle: handleOnIdle,
  });
  

  const login = (invite: string, userType: string, userID: string, email: string, dob?: string) => {
      //api call to check uci, dob, and email
      const body: LoginBody = {
        invite,
        userType,
        userID,
        email,
        dob
      }
      return Call(config.API_URL, "public", "login", body)
      .then(({ response, status}) => {
        const { success, limit } = response;
        if(status !== 200) {
          logger.error(status);
          return "error";
        } else if (success) {
          setAuth(true);
          setFirstName(response.name);
          setUserDetails({invite, userID, email, dob});
          return "accepted";
        } else if(!success && limit) {
          setInviteStatus("limit");
          return "limit";
        } else {
          return "invalid";
        }
      })
    };

  return (
    <AuthContext.Provider
      value={{
        token,
        auth,
        lastLoginTime,
        login,
        logout,
        sessionExpired,
        firstName,
        userDetails,
        resetAuth,
        inviteStatus,
        setInviteStatus,
        isLoggingOut
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
